import React, { useState, useEffect, useRef } from 'react';
import MapPanel from "../basic/MapPanel";
import MapLegend from '../specific/MapLegend';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import { BlurCircular } from '@mui/icons-material';
import {
    buildColoredMapPathsPerWork,
    createMapValueLegends,
    getHistoryPointCoords,
    hasValidHistoryCoords,
    isPulvStatusIniciado,
    parseMapNumericValue,
} from '../../usecases/mapPathUtils';

const shouldIncludePulvItem = (item) => isPulvStatusIniciado(item?.stat);

const getPulvItemsWithCoords = (workData) => {
    const histPulv = workData?.[1]?.histPulv;
    return Array.isArray(histPulv) ? histPulv.filter(hasValidHistoryCoords) : [];
};

const buildLitrosMinPaths = (data, legends) =>
    buildColoredMapPathsPerWork({
        data,
        getItems: getPulvItemsWithCoords,
        legends,
        getValue: (item) => item.lmin,
        getCoords: getHistoryPointCoords,
        shouldIncludeItem: shouldIncludePulvItem,
    });

const Map_litrosmin = ({ data, implementWidthMeters }) => {
    const [legends, setLegends] = useState([]);
    const [path, setPath] = useState([]);
    const [marker, setMarker] = useState([]);
    const [centerMap, setCenterMap] = useState(null);
    const [loadingMap, setLoadingMap] = useState(true);
    const [sliderValue, setSliderValue] = useState([0, 10]);
    const [realMin, setRealMin] = useState(0);
    const [realMax, setRealMax] = useState(10);
    const isInitialLoadRef = useRef(true);

    const createLegends = (newMin, newMax) => {
        const newLegends = createMapValueLegends(newMin, newMax);
        setLegends(newLegends);
        return newLegends;
    };

    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    const applyLimits = () => {
        const [newMin, newMax] = sliderValue;
        createLegends(newMin, newMax);
    };

    const processPath = () => {
        const currentHistory = (data && data[1] && data[1].histPulv) ? data[1].histPulv : [];
        const withCoords = Array.isArray(currentHistory) ? currentHistory.filter(hasValidHistoryCoords) : [];
        if (!withCoords.length) {
            setPath([]);
            return;
        }

        if (!legends || legends.length === 0) {
            return;
        }

        setPath(buildLitrosMinPaths(data, legends));
    };

    const handleLegendChange = (legendId) => {
        const legendsCopy = legends.map((legend) =>
            legend.id === legendId ? { ...legend, show: !legend.show } : legend
        );
        setLegends(legendsCopy);
    };

    useEffect(() => {
        isInitialLoadRef.current = true;
        const currentHistory = (data && data[1] && data[1].histPulv) ? data[1].histPulv : [];
        const withCoords = Array.isArray(currentHistory) ? currentHistory.filter(hasValidHistoryCoords) : [];

        if (withCoords.length > 0) {
            const validLmin = withCoords
                .map(item => parseMapNumericValue(item.lmin))
                .filter(v => v != null);
            const min = validLmin.length
                ? validLmin.reduce((acc, v) => (v < acc ? v : acc), Number.MAX_VALUE)
                : 0;
            const max = validLmin.length
                ? validLmin.reduce((acc, v) => (v > acc ? v : acc), 0)
                : 10;

            const newRealMin = min !== Number.MAX_VALUE ? min : 0;
            const newRealMax = max > newRealMin ? max : newRealMin + 10;

            setRealMin(newRealMin);
            setRealMax(newRealMax);
            setSliderValue([newRealMin, newRealMax]);

            const newLegends = createLegends(newRealMin, newRealMax);
            isInitialLoadRef.current = false;

            setPath(buildLitrosMinPaths(data, newLegends));
            setLoadingMap(false);

            const first = getHistoryPointCoords(withCoords[0]);
            const last = getHistoryPointCoords(withCoords[withCoords.length - 1]);
            setMarker([
                { latitude: first.lat, longitude: first.lng, coordinateType: 'first' },
                { latitude: last.lat, longitude: last.lng, coordinateType: 'last' }
            ]);
            setCenterMap({ lat: first.lat, lng: first.lng });
        } else {
            setPath([]);
            setLoadingMap(false);
        }
    }, [data[1]?.histPulv]);

    useEffect(() => {
        if (isInitialLoadRef.current) {
            return;
        }
        if (legends && legends.length > 0) {
            processPath();
        }
    }, [legends]);

    return (
        <div style={{ width: '100%', alignItems: 'center' }}>
            <h3>Vazão de litros por minuto durante o trabalho (L/min)</h3>
            {loadingMap ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
                    <div className="spinner"></div>
                    <BlurCircular style={{ fontSize: '40px', marginLeft: '20px' }} />
                </div>
            ) : (
                <>
                    <MapPanel markers={marker} zoomMap={17} centerMap={centerMap} linhas={path} height={600} implementWidthMeters={implementWidthMeters} />
                    <MapLegend legends={legends} onLegendChange={handleLegendChange} />
                    <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0' }}>
                        <Slider
                            style={{ width: '60%', marginRight: '20px', marginLeft: '24px' }}
                            value={sliderValue}
                            onChange={handleSliderChange}
                            valueLabelDisplay="auto"
                            min={realMin}
                            max={realMax}
                        />
                        <Button
                            onClick={applyLimits}
                            style={{ backgroundColor: '#ffca20', color: '#000', borderRadius: '5px' }}
                        >
                            Aplicar Limites
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Map_litrosmin;
